import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Check, Eraser, ImagePlus, Loader2, RotateCcw, Trash2 } from "lucide-react";
import api from "../lib/api";
import { recordsError } from "../lib/records";
import { BRAND_COLOR_KEYS, brandSample, isHexColor, sameBrand } from "../lib/brand";
import { Segmented } from "../components/records/BlockEditors";
import RemoveBackgroundDialog from "../components/records/RemoveBackgroundDialog";
import { TemplatePreviewFrame } from "../components/records/TemplateBits";
import { SaveBar } from "./Settings";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const SIZES = Array.from({ length: 7 }, (_, i) => 9 + i * 0.5);
const MARGINS = [["top", "Haut"], ["side", "Côtés"], ["bottom", "Bas"]];

function Group({ title, hint, children, testid }) {
  return (
    <Card className="space-y-3 p-4" data-testid={testid}>
      <div>
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {hint && <p className="text-xs text-slate-500">{hint}</p>}
      </div>
      {children}
    </Card>
  );
}

function ColorRow({ name, label, value, onChange }) {
  const valid = isHexColor(value);
  return (
    <div className="flex items-center gap-2" data-testid={`brand-color-${name}`}>
      <input type="color" value={valid ? value : "#000000"} onChange={(e) => onChange(e.target.value)} aria-label={`${label} : sélecteur de couleur`}
        className="h-9 w-10 shrink-0 cursor-pointer rounded-md border border-slate-200 bg-white p-0.5" />
      <Label htmlFor={`brand-hex-${name}`} className="min-w-0 flex-1 truncate text-sm font-normal">{label}</Label>
      <Input id={`brand-hex-${name}`} value={value} maxLength={7} spellCheck={false} aria-invalid={!valid} onChange={(e) => onChange(e.target.value.trim())}
        className={`h-9 w-28 font-mono text-xs ${valid ? "" : "border-red-400"}`} data-testid={`brand-hex-${name}`} />
    </div>
  );
}

function FontField({ slot, label, fonts, value, onChange }) {
  const font = fonts.find((f) => f.key === value);
  return (
    <div className="space-y-1.5" data-testid={`brand-font-${slot}`}>
      <Label className="text-sm">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-10" aria-label={label} data-testid={`brand-font-${slot}-trigger`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {fonts.map((f) => (
            <SelectItem key={f.key} value={f.key} data-testid={`brand-font-${slot}-${f.key}`}>
              <span style={{ fontFamily: f.stack }}>{f.label}</span>
              <span className="ml-2 text-xs text-slate-400">{f.category === "serif" ? "avec empattement" : "sans empattement"}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {font && <p className="truncate rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700" style={{ fontFamily: font.stack }} data-testid={`brand-font-${slot}-sample`}>{brandSample(slot)}</p>}
    </div>
  );
}

function MediaLibrary({ assets, form, onPick, onChanged }) {
  const [kind, setKind] = useState("logo");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cleaning, setCleaning] = useState(null);
  const input = useRef(null);
  const visible = assets.filter((a) => !a.retired);
  const field = (a) => (a.kind === "stamp" ? "stamp_asset_id" : "logo_asset_id");

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setError("");
    const body = new FormData();
    body.append("file", file);
    body.append("kind", kind);
    body.append("name", name.trim());
    try {
      await api.post("/records/brand/assets", body, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(kind === "logo" ? "Logo ajouté à la médiathèque." : "Cachet ajouté à la médiathèque.");
      setName("");
      await onChanged();
    } catch (e) {
      setError(recordsError(e, "Envoi impossible. Réessayez."));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };
  const retire = async (asset) => {
    try {
      await api.post(`/records/brand/assets/${asset.id}/retire`);
      toast.success("Image retirée : elle reste affichée dans les modèles qui l'utilisent.");
      await onChanged();
    } catch (e) {
      toast.error(recordsError(e, "Retrait impossible."));
    }
  };

  return (
    <div className="space-y-3" data-testid="brand-library">
      {visible.length === 0 && <p className="text-sm text-slate-500" data-testid="brand-library-empty">Aucune image pour l'instant. Ajoutez le logo de la société : il apparaîtra dans l'en-tête des documents.</p>}
      <div className="grid grid-cols-1 gap-2">
        {visible.map((a) => {
          const current = form[field(a)] === a.id;
          return (
            <div key={a.id} className={`flex items-center gap-3 rounded-lg border p-2 ${current ? "border-jamora ring-1 ring-jamora" : "border-slate-200"}`} data-testid={`brand-asset-${a.id}`}>
              <div className="flex h-16 w-20 shrink-0 items-center justify-center rounded bg-slate-50">
                {a.thumb && <img src={a.thumb} alt="" className="max-h-14 max-w-[4.5rem] object-contain" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-800">{a.name}</p>
                <p className="text-xs text-slate-500">{a.kind === "stamp" ? "Cachet" : "Logo"} · {a.width}×{a.height}</p>
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  {current ? (
                    <>
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-jamora"><Check size={12} />{a.kind === "stamp" ? "Cachet" : "Logo"} de la charte</span>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => onPick(field(a), null)} data-testid={`brand-asset-${a.id}-unset`}>Ne plus l'utiliser</Button>
                    </>
                  ) : (
                    <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => onPick(field(a), a.id)} data-testid={`brand-asset-${a.id}-use`}>Utiliser</Button>
                  )}
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setCleaning(a)} data-testid={`brand-asset-${a.id}-remove-bg`}><Eraser size={12} className="mr-1" />Retirer le fond</Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500 hover:text-red-600" aria-label={`Retirer ${a.name}`} disabled={current}
                    title={current ? "Cette image sert dans la charte" : undefined} onClick={() => retire(a)} data-testid={`brand-asset-${a.id}-retire`}><Trash2 size={13} /></Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="space-y-2 rounded-lg border border-dashed border-slate-300 p-3">
        <p className="text-xs font-medium text-slate-600">Ajouter une image</p>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented value={kind} choices={[["logo", "Logo"], ["stamp", "Cachet"]]} onChange={setKind} label="Nature de l'image" testid="brand-upload-kind" />
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="Nom de l'image (facultatif)" aria-label="Nom de l'image" className="order-last h-9 basis-full" />
          <Button variant="outline" size="sm" disabled={busy} onClick={() => input.current?.click()} data-testid="brand-upload">
            {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <ImagePlus size={14} className="mr-1.5" />}Choisir un fichier
          </Button>
          <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="brand-upload-input" onChange={(e) => upload(e.target.files?.[0])} />
        </div>
        <p className="text-xs text-slate-500">PNG, JPEG ou WebP, 2 Mo au plus. Les SVG ne sont pas acceptés.</p>
        {error && <p className="text-sm text-red-600" role="alert" data-testid="brand-upload-error">{error}</p>}
      </div>
      {cleaning && <RemoveBackgroundDialog asset={cleaning} onClose={() => setCleaning(null)} onCreated={onChanged} />}
    </div>
  );
}

export default function RecordBrand() {
  const [state, setState] = useState(null);
  const [form, setForm] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [preview, setPreview] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [previewError, setPreviewError] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("settings");
  const seq = useRef(0);

  const apply = useCallback((data) => {
    setState(data);
    setForm(data.brand);
    setAssets(data.assets);
    setWarnings(data.warnings || []);
  }, []);
  const load = useCallback(async () => {
    setLoadError("");
    try {
      const { data } = await api.get("/records/brand");
      apply(data);
    } catch (e) {
      setLoadError(recordsError(e, "Charte introuvable."));
    }
  }, [apply]);
  useEffect(() => { load(); }, [load]);

  // The font selector shows each name in its own letters: the same files the PDF embeds, loaded once into the page.
  useEffect(() => {
    if (document.getElementById("brand-fonts-css")) return;
    api.get("/records/brand/fonts.css", { responseType: "text", transformResponse: (r) => r }).then(({ data }) => {
      if (document.getElementById("brand-fonts-css")) return;
      const style = document.createElement("style");
      style.id = "brand-fonts-css";
      style.textContent = data;
      document.head.appendChild(style);
    }).catch(() => {});
  }, []);

  const colorsValid = form ? BRAND_COLOR_KEYS.every((k) => isHexColor(form.colors[k])) : false;
  const dirty = !!form && !!state && !sameBrand(form, state.brand);

  // The planche de style follows the settings (unsaved ones included) after a short pause.
  useEffect(() => {
    if (!form || !colorsValid) return undefined;
    const mine = ++seq.current;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/records/brand/preview", { brand: form, paged: true });
        if (mine === seq.current) { setPreview(data); setWarnings(data.warnings || []); setPreviewError(""); }
      } catch (e) {
        if (mine === seq.current) setPreviewError(recordsError(e, "Aperçu indisponible."));
      } finally {
        if (mine === seq.current) setPreviewLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form, colorsValid]);

  const refreshAssets = useCallback(async () => {
    const { data } = await api.get("/records/brand/assets", { params: { include_retired: true } });
    setAssets(data.items);
  }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/records/brand", { expected_revision: state.revision, brand: form });
      apply(data);
      toast.success("Charte enregistrée : elle sert aux prochains documents. Les PDF déjà générés ne changent pas.");
    } catch (e) {
      if (e?.response?.status === 409 && e.response.data?.detail?.code === "version_conflict") {
        toast.error("Quelqu'un a modifié la charte entre-temps : elle a été rechargée, vos modifications sont perdues.");
        await load();
      } else {
        toast.error(recordsError(e, "Enregistrement impossible."));
      }
    } finally {
      setSaving(false);
    }
  };
  const fonts = state?.fonts || [];
  const numberings = useMemo(() => Object.entries(state?.numberings || {}), [state]);

  if (loadError) {
    return (
      <Card className="space-y-3 p-6 text-center" data-testid="brand-error">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700">{loadError}</p>
        <Button variant="outline" onClick={load}><RotateCcw size={14} className="mr-1.5" />Réessayer</Button>
      </Card>
    );
  }
  if (!form || !state) return <div className="space-y-3"><Skeleton className="h-10 w-1/3" /><Skeleton className="h-96 w-full" /></div>;

  const settings = (
    <div className="space-y-4" data-testid="brand-settings">
      <Group title="Couleurs" hint="Elles habillent les titres, les tableaux, les encadrés et les filets de tous les documents." testid="brand-colors">
        <div className="space-y-2">
          {BRAND_COLOR_KEYS.map((k) => <ColorRow key={k} name={k} label={state.color_labels[k]} value={form.colors[k]} onChange={(v) => set({ colors: { ...form.colors, [k]: v } })} />)}
        </div>
        {!colorsValid && <p className="text-sm text-red-600" role="alert">Une couleur n'est pas au format #rrggbb.</p>}
        {previewError && colorsValid && <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="brand-contrast-error">{previewError}</p>}
        {!previewError && warnings.length > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="brand-contrast-warning">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>Contraste juste : {warnings.map((w) => `${w.label.toLowerCase()} (${w.ratio}:1)`).join(" ; ")}. Le texte reste lisible mais plus fatigant ; 4,5:1 est recommandé.</span>
          </div>
        )}
      </Group>

      <Group title="Typographie" hint="Les polices sont celles de la liste, embarquées dans les PDF : l'aperçu montre exactement les mêmes lettres." testid="brand-fonts">
        <FontField slot="body" label="Police du texte" fonts={fonts} value={form.fonts.body} onChange={(v) => set({ fonts: { ...form.fonts, body: v } })} />
        <FontField slot="headings" label="Police des titres" fonts={fonts} value={form.fonts.headings} onChange={(v) => set({ fonts: { ...form.fonts, headings: v } })} />
        <div className="space-y-1.5">
          <Label htmlFor="brand-size" className="text-sm">Taille du texte</Label>
          <Select value={String(form.base_size)} onValueChange={(v) => set({ base_size: Number(v) })}>
            <SelectTrigger id="brand-size" className="h-10 w-40" data-testid="brand-size"><SelectValue /></SelectTrigger>
            <SelectContent>{SIZES.map((s) => <SelectItem key={s} value={String(s)}>{String(s).replace(".", ",")} pt</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Group>

      <Group title="Page" hint="Marges de la feuille A4 et numérotation des articles." testid="brand-group-page">
        <div className="grid grid-cols-3 gap-2">
          {MARGINS.map(([key, label]) => {
            const [low, high] = state.bounds.margins[key];
            return (
              <div key={key} className="space-y-1.5">
                <Label htmlFor={`brand-margin-${key}`} className="text-sm">{label} (mm)</Label>
                <Input id={`brand-margin-${key}`} type="number" inputMode="numeric" min={low} max={high} value={form.margins[key]} className="h-10"
                  onChange={(e) => set({ margins: { ...form.margins, [key]: e.target.value === "" ? "" : Number(e.target.value) } })} data-testid={`brand-margin-${key}`} />
              </div>
            );
          })}
        </div>
        <div className="space-y-1.5">
          <p className="text-sm font-medium">Numérotation des articles</p>
          <Segmented value={form.numbering} choices={numberings} onChange={(v) => set({ numbering: v })} label="Numérotation des articles" testid="brand-numbering" />
        </div>
      </Group>

      <Group title="Logo et cachet" hint="Le logo ouvre l'en-tête des documents ; le cachet peut figurer dans le bloc de signatures." testid="brand-media">
        <MediaLibrary assets={assets} form={form} onPick={(field, id) => set({ [field]: id })} onChanged={refreshAssets} />
      </Group>
    </div>
  );
  const sheet = (
    <div className="space-y-2" data-testid="brand-preview-pane">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-900">Planche de style</h2>
        <span className="text-xs text-slate-500">Un document type avec tous les blocs.</span>
      </div>
      <TemplatePreviewFrame preview={preview} loading={previewLoading} error="" testid="brand-preview" maxHeightClassName="lg:max-h-[calc(100vh-9rem)] lg:overflow-y-auto" />
    </div>
  );

  return (
    <div className="space-y-4 pb-4" data-testid="brand-page">
      <div className="space-y-1">
        <h1 className="font-display text-2xl font-bold text-slate-900">Charte graphique</h1>
        <p className="text-sm text-slate-500">
          Couleurs, polices et logo appliqués à tous les documents générés depuis un <Link to="/documents/modeles" className="underline underline-offset-2">modèle</Link>.
          Révision {state.revision || "initiale (valeurs Jamora)"}{state.updated_by_name ? ` · modifiée par ${state.updated_by_name}` : ""}.
        </p>
      </div>
      <div className="flex gap-1 lg:hidden" role="tablist" aria-label="Réglages ou aperçu">
        {[["settings", "Réglages"], ["preview", "Aperçu"]].map(([value, label]) => (
          <button key={value} type="button" role="tab" aria-selected={tab === value} onClick={() => setTab(value)} data-testid={`brand-tab-${value}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ${tab === value ? "bg-jamora text-white" : "border border-slate-200 bg-white text-slate-600"}`}>{label}</button>
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,28rem)_minmax(0,1fr)] lg:items-start">
        <div className={`min-w-0 ${tab === "settings" ? "" : "hidden lg:block"}`}>{settings}</div>
        <div className={`min-w-0 lg:sticky lg:top-4 ${tab === "preview" ? "" : "hidden lg:block"}`}>{sheet}</div>
      </div>
      <SaveBar dirty={dirty} saving={saving} canManage saveDisabled={!colorsValid || !!previewError} onSave={save} onRevert={() => { setForm(state.brand); setPreviewError(""); }} />
    </div>
  );
}
